/**
 * Modal for confirming access with PIN verification (e.g. Settings, Edit terminal).
 * Same PIN validation as LogoutModal but calls onConfirm on success instead of logging out.
 */

import { useState } from 'react';
import { createPhotoBoothAPI } from '@photobooth/api-client';
import { getEffectiveApiBaseUrl } from '../lib/config/terminal-urls';

export interface PinConfirmModalProps {
  outletId: string;
  /** PIN role for validation - 'capture' for Capture terminal, 'customer' for Order/Edit/Settings when customer role */
  pinRole: 'customer' | 'capture';
  title: string;
  message: string;
  confirmLabel?: string;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
}

export function PinConfirmModal({
  outletId,
  pinRole,
  title,
  message,
  confirmLabel = 'Confirm',
  onClose,
  onConfirm,
}: PinConfirmModalProps) {
  const [pin, setPin] = useState('');
  const [showPin, setShowPin] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (pin.length < 4 || pin.length > 6) {
      setError('PIN must be 4-6 digits');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const apiClient = createPhotoBoothAPI({
        baseUrl: getEffectiveApiBaseUrl(),
      });

      const response = await apiClient.outlets.validatePin(outletId, pin, pinRole);

      if (!response.success || !response.data?.valid) {
        setError(response.data?.message ?? 'Invalid PIN. Please try again.');
        setPin('');
        setIsLoading(false);
        return;
      }

      await onConfirm();
    } catch (err) {
      console.error('PIN validation error:', err);
      setError('Failed to validate PIN. Please try again.');
      setPin('');
      setIsLoading(false);
    }
  };

  const handlePinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = String(e.target.value || '')
      .replace(/\D/g, '')
      .slice(0, 6);
    setPin(value);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full">
        <h3 className="text-2xl font-bold text-gray-800 mb-2">{title}</h3>
        <p className="text-gray-600 mb-4">{message}</p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Outlet PIN
            </label>
            <div className="relative">
              <input
                type={showPin ? 'text' : 'password'}
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                value={pin}
                onChange={handlePinChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500 text-center tracking-[0.5em] text-lg"
                disabled={isLoading}
              />
              <button
                type="button"
                onClick={() => setShowPin((prev) => !prev)}
                className="absolute inset-y-0 right-3 flex items-center text-sm text-gray-500 hover:text-gray-700"
              >
                {showPin ? 'Hide' : 'Show'}
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">4–6 digits</p>
          </div>

          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {error}
            </div>
          )}

          <div className="flex gap-3 mt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="flex-1 px-4 py-3 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg font-semibold transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 px-4 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-semibold transition-colors disabled:opacity-50"
            >
              {isLoading ? 'Verifying...' : confirmLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
